import { AnimationMetadata, AnimationBuilder, animate, AnimationFactory } from '@angular/animations';
import { Injectable, NgZone, Renderer2 } from '@angular/core';

import { AbstractNgAnimationService } from '../animation-service.abstract';
import { NG_EVENT_TRIGGERS, INgEventTriggers } from '../event-animation.data';
import { INgTimedAnimationParams, INgAnimationTimeout } from '../event-animation.interface';


/**------------------------------------------------------
 * Timed Animation
 * ---------------
 * > The timed animation will start by the start event and
 * > will continue till the "timeout" time has been reached.
 * > So the animation has a fix time of length how long the
 * > animation will continue till it stops.
 * > Note: If you want to complete one animation cycle then
 * > animation duration and timeout needs to be same.
 */
@Injectable()
export class NgTimedAnimationService extends AbstractNgAnimationService {

	//** Configurations */
	protected override readonly DEFAULT_TIMING: INgAnimationTimeout = {
		duration	: 250,
		delay		: 0,
		timeout		: 250
	};

	//** Helper Variables */
	private stopOnTimeout	: boolean = true;

	constructor(
		private animationBuilder: AnimationBuilder,
		private renderer		: Renderer2,
		private ngZone			: NgZone
	) {
		super();
	}


	/**------------------------------------------------------
	 * Setup the Animation
	 */
	setupAnimation(params: INgTimedAnimationParams): void {

		//0 - define the options
		params = this.verifyParameters(params, this.DEFAULT_TIMING);

		//1 - prepare start animation effect
		const startAnimationMetadata: AnimationMetadata = animate(`${params.timing.duration}ms ${params.timing.delay}ms`, params.states.start);

		//2 - listen to the events on the element
		const triggers: INgEventTriggers = NG_EVENT_TRIGGERS[params.event];
		this.renderer.listen(params.triggerElement, triggers.start, () => {

			//a. if animation started do nothing
			if (!this.stopOnTimeout) return;

			//b. reset stop on timeout to false
			this.stopOnTimeout = false;

			//c. start animation effect
			this.animateEffect(params.targetElement, [startAnimationMetadata]);

			//d. stop animation after time interval
			this.ngZone.runOutsideAngular(() => {
				setTimeout(() => {
					this.stopOnTimeout = true;
					this.animationPlayer.finish();
				}, params.timing.timeout);
			});
		});
	}


	/**------------------------------------------------------
	 * Helper Function
	 */
	private animateEffect(targetElement: HTMLElement, animationMetadata: AnimationMetadata[]) {

		//0 - create the animation player
		const animationFactory: AnimationFactory = this.animationBuilder.build(animationMetadata);
		this.animationPlayer = animationFactory.create(targetElement);

		//1 - on animation done, destroy and create animation again
		this.animationPlayer.onDone(() => {

			//a. if a previous one exist, destroy it
			this.animationPlayer?.destroy();

			//b. animate effect again if no stop event occurs
			if (!this.stopOnTimeout) this.animateEffect(targetElement, animationMetadata);
		});

		//2 - animate the effect
		this.animationPlayer.play();
	}

	protected verifyCustomParameters(params: INgTimedAnimationParams): INgTimedAnimationParams {
		if (!params?.states?.start) 	 throw new Error(`NgTimedAnimationService => verifyParameters => FATAL ERROR: The animation states are required for defining the animation behavior, the starting state is undefined (start animation state: ${params?.states?.start})`);
		if (params.timing.timeout! <= 0) throw new Error(`NgTimedAnimationService => verifyParameters => FATAL ERROR: The duration for the animation can not be smaller or equal to 0 (provided value: ${params.timing.duration})`);
		return params;
	}
}
